import { GAME_IDS, type GameId } from '../types';
import type { VariantRules } from './base';
import { doubleKlondikeTurnOne, doubleKlondikeTurnThree, klondikeTurnOne, klondikeTurnThree } from './klondike';
import { spiderFourSuits, spiderOneSuit, spiderTwoSuits } from './spider';
import { fortyThieves } from './fortyThieves';
import { freecell } from './freecell';
import { pyramid } from './pyramid';
import { mahjong, MAHJONG_LAYOUTS, type Layout } from './mahjong';
import { mahjongConnect } from './mahjongConnect';
import { hearts } from './hearts';
import { makao } from './makao';
import { threeFiveEight } from './threeFiveEight';
import { thousand } from './thousand';
import { kulki } from './kulki';

export type { VariantRules } from './base';
export { suitsFromPattern } from './spider';

export const RULES: Record<GameId, VariantRules> = {
  'klondike:turn-one': klondikeTurnOne,
  'klondike:turn-three': klondikeTurnThree,
  'double-klondike:turn-one': doubleKlondikeTurnOne,
  'double-klondike:turn-three': doubleKlondikeTurnThree,
  'spider:one-suit': spiderOneSuit,
  'spider:two-suits': spiderTwoSuits,
  'spider:four-suits': spiderFourSuits,
  'forty-thieves': fortyThieves,
  freecell,
  pyramid,
  mahjong,
  'mahjong-connect': mahjongConnect,
  hearts,
  makao,
  'three-five-eight': threeFiveEight,
  thousand,
  kulki,
};

export const DEFAULT_GAME: GameId = 'klondike:turn-one';

/** The tile games: the layout of this deal (Mahjong's shape, Connect's grid); the Turtle for anything else. */
export const layoutOf = (s: { game: GameId; layout?: number }): Layout => RULES[s.game].layouts?.[s.layout ?? 0] ?? RULES[s.game].layouts?.[0] ?? MAHJONG_LAYOUTS[0]!;

/**
 * Turn any stored or URL value into one of the GAME_IDS: lower case, trimmed; `#klondike` (no variant)
 * -> turn-one, `#spider` and `#spider:<unknown>` -> four suits; anything else unknown -> klondike:turn-one.
 * Fixes FIX-009 / GAP-010 / KL-B05 / `#base` (legacy built types from case variants and crashed on `base`).
 */
export function normalizeGameId(raw: unknown): GameId {
  if (typeof raw !== 'string') return DEFAULT_GAME;
  const v = raw.trim().toLowerCase().replace(/^#/, '');
  if ((GAME_IDS as readonly string[]).includes(v)) return v as GameId;
  if (v === 'klondike') return 'klondike:turn-one';
  if (v === 'double-klondike') return 'double-klondike:turn-one';
  if (v === 'spider' || v.startsWith('spider:')) return 'spider:four-suits'; // any unknown Spider variant: four suits (SPIDER-013)
  if (v === 'fortythieves') return 'forty-thieves'; // old #fortyThieves bookmarks (legacy alias)
  if (v === 'free-cell' || v === 'freecel') return 'freecell';
  if (v === 'piramida') return 'pyramid';
  if (v === 'mahjongg' || v === 'madzong') return 'mahjong';
  if (v === 'connect' || v === 'mahjongconnect' || v === 'mahjong_connect' || v === 'shisen' || v === 'shisen-sho' || v === 'shisensho') return 'mahjong-connect';
  if (v === 'kierki') return 'hearts';
  if (v === 'macao') return 'makao';
  if (v === 'lines' || v === 'marbles' || v === 'balls') return 'kulki';
  if (v === '358' || v === '3-5-8' || v === 'trzy-piec-osiem') return 'three-five-eight';
  if (v === '1000' || v === 'tysiac' || v === 'tysiąc') return 'thousand';
  return DEFAULT_GAME;
}
